import { useState, useEffect, useCallback, useRef } from "react";
import api, { presignedUpload } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Skeleton } from "../components/ui/skeleton";
import { Mic2, Plus, Pencil, Trash2, CheckCircle2, AlertCircle, Play, Upload, X, Loader2, AudioLines } from "lucide-react";
import { toast } from "sonner";

export default function Voices() {
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("id");
  const [name, setName] = useState("");
  const [voiceId, setVoiceId] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get("/voices");
      setVoices(res.data);
    } catch { toast.error("Failed to load voices"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditing(null); setName(""); setVoiceId(""); setFile(null); setTab("id"); setShowDialog(true);
  };
  const openEdit = (v) => {
    setEditing(v); setName(v.name); setVoiceId(v.voice_id || ""); setFile(null);
    setTab(v.cloned ? "clone" : "id"); setShowDialog(true);
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("audio/")) { toast.error("Please select an audio file"); return; }
    setFile(f);
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (tab === "clone") {
        if (!file && !editing) { toast.error("Please upload a voice sample"); setSaving(false); return; }
        if (file) {
          // Upload audio directly to S3 via presigned URL (bypasses API Gateway 10MB limit)
          const { s3_url } = await presignedUpload(file);
          await api.post("/voices/clone-from-url", { name, audio_url: s3_url });
          toast.success("Voice cloned successfully via ElevenLabs!");
        }
      } else {
        if (editing) {
          await api.put(`/voices/${editing.id}`, { name, provider: "elevenlabs", voice_id: voiceId });
          toast.success("Voice updated");
        } else {
          await api.post("/voices", { name, provider: "elevenlabs", voice_id: voiceId });
          toast.success("Voice created");
        }
      }
      setShowDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save voice");
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/voices/${id}`);
      setVoices(prev => prev.filter(v => v.id !== id));
      toast.success("Voice deleted");
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="voices-page">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <Mic2 size={24} className="text-[#4D11F6]" strokeWidth={1.5} />
          <div>
            <h1 className="text-3xl font-bold text-[#020B23]" style={{ fontFamily: 'Sora, sans-serif' }}>Voices</h1>
            <p className="text-[#676970] text-sm mt-0.5">Configure narration voices via ElevenLabs</p>
          </div>
        </div>
        <Button onClick={openCreate} data-testid="create-voice-btn"
          className="bg-[#4D11F6] hover:bg-[#3A0BC0] text-white h-10 px-5">
          <Plus size={16} className="mr-2" /> New Voice
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-20 w-full rounded-xl" />)}</div>
      ) : voices.length === 0 ? (
        <div className="flex flex-col items-center py-20 text-center" data-testid="voices-empty">
          <div className="w-14 h-14 rounded-2xl bg-[#4D11F6]/10 flex items-center justify-center mb-4">
            <Mic2 size={24} className="text-[#4D11F6]" />
          </div>
          <h3 className="text-lg font-semibold text-[#020B23] mb-2" style={{ fontFamily: 'Sora, sans-serif' }}>No voices yet</h3>
          <p className="text-[#676970] text-sm mb-5 max-w-xs">Add an ElevenLabs voice by ID or clone from a sample.</p>
          <Button onClick={openCreate} className="bg-[#4D11F6] hover:bg-[#3A0BC0] text-white">
            <Plus size={16} className="mr-2" /> Create Voice
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {voices.map((voice, idx) => (
            <div key={voice.id} data-testid={`voice-card-${voice.id}`}
              className="bg-white rounded-xl border border-[#E2E4E7] p-5 flex items-center gap-4 hover:shadow-md transition-shadow animate-fade-in group"
              style={{ animationDelay: `${idx * 40}ms` }}>
              <div className="w-12 h-12 rounded-xl bg-[#020B23] flex items-center justify-center shrink-0">
                {voice.cloned ? <AudioLines size={20} className="text-[#7A81E9]" /> : <Mic2 size={20} className="text-white" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-[#020B23]" style={{ fontFamily: 'Sora, sans-serif' }}>{voice.name}</h3>
                  {voice.cloned && <Badge className="text-xs bg-[#4D11F6]/10 text-[#4D11F6] border-[#4D11F6]/20">Cloned</Badge>}
                </div>
                <p className="text-xs text-[#94969C] mt-0.5">
                  {voice.provider} / {voice.voice_id || "No ID configured"}
                </p>
              </div>
              <Badge className={voice.status === 'Ready' ? 'bg-emerald-50 text-emerald-700 border-emerald-100 text-xs' : 'bg-amber-50 text-amber-700 border-amber-100 text-xs'}>
                {voice.status === 'Ready' ? <CheckCircle2 size={11} className="mr-1" /> : <AlertCircle size={11} className="mr-1" />}
                {voice.status}
              </Badge>
              <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button variant="ghost" size="icon" onClick={() => openEdit(voice)} className="h-8 w-8 text-[#94969C] hover:text-[#4D11F6]">
                  <Pencil size={14} />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(voice.id)} className="h-8 w-8 text-[#94969C] hover:text-[#F5354E]">
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg bg-white" data-testid="voice-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Sora, sans-serif' }} className="text-[#020B23]">
              {editing ? "Edit Voice" : "Create Voice"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-[#1A2238] font-medium">Voice Name</Label>
              <Input placeholder="e.g., Professional Male" value={name} onChange={(e) => setName(e.target.value)}
                data-testid="voice-name-input" className="h-10 bg-white border-[#E2E4E7]" />
            </div>

            <Tabs value={tab} onValueChange={setTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-[#F3F3F9]">
                <TabsTrigger value="id" data-testid="voice-tab-id" className="text-sm data-[state=active]:bg-white data-[state=active]:text-[#4D11F6]">
                  ElevenLabs Voice ID
                </TabsTrigger>
                <TabsTrigger value="clone" data-testid="voice-tab-clone" className="text-sm data-[state=active]:bg-white data-[state=active]:text-[#4D11F6]">
                  Clone from Sample
                </TabsTrigger>
              </TabsList>

              <TabsContent value="id" className="space-y-3 mt-4">
                <div className="space-y-2">
                  <Label className="text-[#1A2238] font-medium">ElevenLabs Voice ID</Label>
                  <Input placeholder="e.g., pNInz6obpgDQGcFmaJgB" value={voiceId} onChange={(e) => setVoiceId(e.target.value)}
                    data-testid="voice-id-input" className="h-10 bg-white border-[#E2E4E7] font-mono text-sm" />
                  <p className="text-xs text-[#94969C]">Find voice IDs in your ElevenLabs dashboard under Voices.</p>
                </div>
              </TabsContent>

              <TabsContent value="clone" className="space-y-3 mt-4">
                <div className="space-y-2">
                  <Label className="text-[#1A2238] font-medium">Voice Sample (audio)</Label>
                  <div
                    onClick={() => fileRef.current?.click()}
                    data-testid="voice-sample-upload"
                    className={`border-2 border-dashed rounded-xl cursor-pointer transition-colors p-6 text-center ${
                      file ? "border-[#4D11F6]/30 bg-[#4D11F6]/5" : "border-[#E2E4E7] hover:border-[#4D11F6]/40 bg-[#F3F3F9]"
                    }`}
                  >
                    {file ? (
                      <div className="flex items-center justify-center gap-3">
                        <AudioLines size={20} className="text-[#4D11F6]" />
                        <span className="text-sm text-[#1A2238] font-medium">{file.name}</span>
                        <button onClick={(e) => { e.stopPropagation(); setFile(null); }}
                          className="w-6 h-6 rounded-full bg-[#F5354E]/10 text-[#F5354E] flex items-center justify-center hover:bg-[#F5354E]/20">
                          <X size={12} />
                        </button>
                      </div>
                    ) : (
                      <div>
                        <Upload size={24} className="mx-auto text-[#94969C] mb-2" />
                        <p className="text-sm font-medium text-[#1A2238]">Upload voice sample</p>
                        <p className="text-xs text-[#94969C] mt-1">MP3 or WAV, 10-30 seconds recommended</p>
                      </div>
                    )}
                    <input ref={fileRef} type="file" accept="audio/*" className="hidden" onChange={handleFileChange} />
                  </div>
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-[#E6EBFF] border border-[#CACDFE]">
                    <AlertCircle size={14} className="text-[#4D11F6] mt-0.5 shrink-0" />
                    <p className="text-xs text-[#34364F]">
                      Uses ElevenLabs Instant Voice Cloning. Requires an ElevenLabs API key configured in Integrations.
                    </p>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="border-[#E2E4E7]">Cancel</Button>
            <Button onClick={handleSave} disabled={saving || !name.trim()} data-testid="save-voice-btn"
              className="bg-[#4D11F6] hover:bg-[#3A0BC0] text-white">
              {saving ? (
                <><Loader2 size={14} className="mr-2 animate-spin" /> {tab === "clone" ? "Cloning voice..." : "Saving..."}</>
              ) : editing ? "Update" : tab === "clone" ? "Clone Voice" : "Create Voice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
